/**
 * One-based pagination shared by the footer and client-side data slicing.
 * Totals/sizes must be validated by the caller; an unknown total is not zero.
 */
export function getPaginationState({ page, pageSize, totalItems }) {
  if (!Number.isSafeInteger(pageSize) || pageSize < 1) {
    throw new RangeError("pageSize must be a positive integer");
  }
  if (!Number.isSafeInteger(totalItems) || totalItems < 0) {
    throw new RangeError("totalItems must be a non-negative integer");
  }
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const requestedPage = Number.isSafeInteger(page) ? page : 1;
  const safePage = Math.min(Math.max(requestedPage, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  return {
    totalPages,
    safePage,
    startIndex,
    endIndex,
    startItem: totalItems === 0 ? 0 : startIndex + 1,
    endItem: endIndex,
  };
}

/** At most seven positions, with non-interactive gaps between distant pages. */
export function getPaginationItems(page, totalPages) {
  const range = (start, end) =>
    Array.from({ length: end - start + 1 }, (_, index) => start + index);
  if (totalPages <= 7) return range(1, totalPages);
  if (page <= 4) return [...range(1, 5), "ellipsis-end", totalPages];
  if (page >= totalPages - 3) {
    return [1, "ellipsis-start", ...range(totalPages - 4, totalPages)];
  }
  return [1, "ellipsis-start", page - 1, page, page + 1, "ellipsis-end", totalPages];
}
